import React, { useEffect } from 'react';
import { css } from '@emotion/react';

// types
import { PLPFilterOptions } from 'pages/WebsiteDesignsPLPSSR/helpers';

// hooks
import useScrollLock from '@zola/zola-ui/src/hooks/useScrollLock';

// redux
import { useAppSelector } from 'reducers/useAppSelector';

// utils + helpers
import capitalizeFirst from '@zola-helpers/client/dist/es/transformers/capitalizeFirst';
import { SEGMENTS, segments } from 'pages/WebsiteDesignsPLPV2/utils';

// components
import { BaseSideDrawer } from '@zola/zola-ui/src/components/SideDrawer';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import { CheckboxField } from '@zola/zola-ui/src/components/Form/CheckboxField';
import { CloseIcon } from '@zola/zola-ui/src/components/SvgIcons/Close';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import { RadioField } from '@zola/zola-ui/src/components/Form/RadioFieldV2/RadioField';
import AccordionV2 from '@zola/zola-ui/src/components/AccordionV2/AccordionV2';

// styles
import { hideWhenDesktop } from 'pages/WebsiteDesignsPLPV2/WebsiteDesignsPLPV2.styles';
import {
  FilterSortHeader,
  FilterSortSubheader,
  HeaderTxt,
  PLPMobileControlsContainer,
  PLPMobileControlsFooter,
  PLPMobileControlsHeader,
  PLPMobileControlsInnerContainer,
  RadioFieldsContainer,
  CheckboxesContainer,
} from './PLPMobileControls.styles';

export type PLPMobileControlsProps = {
  layoutType: string | SEGMENTS;
  handleToggleMobileMenu: () => void;
  mobileMenuVisible: boolean;
  onSort: (sort: string | number | null) => void;
  onToggleLayout: (key: string | SEGMENTS) => void;
  onUpdateFilter: (filters: PLPFilterOptions) => void;
  setLayoutType: (key: string | SEGMENTS) => void;
};

const PLPMobileControls: React.FC<PLPMobileControlsProps> = ({
  layoutType,
  handleToggleMobileMenu,
  mobileMenuVisible,
  onSort,
  onToggleLayout,
  onUpdateFilter,
  setLayoutType,
}) => {
  const resultsCount = useAppSelector(state => state.websitePlp.displayable_total);
  const currentSort = useAppSelector(state => state.websitePlp.applied_sort);
  const currentFilters = useAppSelector(state => state.websitePlp.applied_filters);
  const sortOptions = useAppSelector(state => state.websitePlp.sorts);
  const facets = useAppSelector(state => state.websitePlp.facets);

  const filteredFacets = facets.filter(facet => facet.field !== 'layout_types');

  const { colors = [], motifs = [] } = currentFilters;
  const facetsMap: Record<string, Record<string, string>> = {};
  filteredFacets.forEach(facetGroup => {
    if (facetGroup && facetGroup.field) {
      facetsMap[facetGroup.field] = {};
      if (facetGroup.buckets && facetGroup.buckets.length > 0) {
        facetGroup.buckets.forEach(facetOption => {
          // TODO: Need to figure out how to make TS happy with this nested structure
          // @ts-expect-error ts-migrate(2538) FIXME: Type 'undefined' cannot be used as an index type.
          facetsMap[facetGroup.field][facetOption.value] = facetOption.name;
        });
      }
    }
  });

  const handleClickClear = () => {
    onUpdateFilter({ colors: [], motifs: [], special_features: [] });
    handleToggleMobileMenu();
  };

  const getSortTxt = () => {
    switch (`${currentSort}`) {
      case 'POPULARITY':
        return 'Popularity';
      case 'RECENTLY_ADDED':
        return 'Recently Added';
      default:
        return 'Featured';
    }
  };

  const getNormalizedFilterTxt = (filters: string[]): string => {
    return filters.map(filter => capitalizeFirst(filter, 1).replace(/_/g, ' ')).join(', ');
  };

  const getLayoutTxt = () => {
    return layoutType === SEGMENTS.multipage ? 'Multi page' : 'Single page';
  };

  const handleToggleLayoutType = (key: string | SEGMENTS) => {
    setLayoutType(key);
    onToggleLayout(key);
  };

  const handleClickStyle = (clickedStyle: string) => {
    if (motifs.includes(clickedStyle)) {
      const updatedMotifs = motifs.filter(motif => motif !== clickedStyle);
      onUpdateFilter({ colors, motifs: updatedMotifs, special_features: [] });
    } else {
      const motifsWithNew = [...motifs, clickedStyle];
      onUpdateFilter({ colors, motifs: motifsWithNew, special_features: [] });
    }
  };

  const handleClickColor = (clickedColor: string) => {
    if (colors.includes(clickedColor)) {
      const updatedColors = colors.filter(color => color !== clickedColor);
      onUpdateFilter({ colors: updatedColors, motifs, special_features: [] });
    } else {
      const colorsWithNew = [...colors, clickedColor];
      onUpdateFilter({ colors: colorsWithNew, motifs, special_features: [] });
    }
  };

  const renderStyleCheckboxes = () => {
    const motifKeys = Object.keys(facetsMap.motifs);
    return (
      <CheckboxesContainer data-testid="PLPMobileControls__style-options">
        {motifKeys.map(key => (
          <CheckboxField
            key={`PLPMobileControls__Style--${key}`}
            label={facetsMap.motifs[key]}
            id={key}
            type="checkbox"
            checked={motifs.includes(key)}
            onChange={() => handleClickStyle(key)}
          />
        ))}
      </CheckboxesContainer>
    );
  };

  const renderColorCheckboxes = () => {
    const colorKeys = Object.keys(facetsMap.colors);
    return (
      <CheckboxesContainer data-testid="PLPMobileControls__color-options">
        {colorKeys.map(key => (
          <CheckboxField
            key={`PLPMobileControls__Color--${key}`}
            label={facetsMap.colors[key]}
            id={key}
            type="checkbox"
            checked={colors.includes(key)}
            onChange={() => handleClickColor(key)}
          />
        ))}
      </CheckboxesContainer>
    );
  };

  const { onDisableLock, onEnableLock } = useScrollLock();

  useEffect(() => {
    if (mobileMenuVisible) {
      onEnableLock();
    } else {
      onDisableLock();
    }
  }, [mobileMenuVisible]);

  return (
    <BaseSideDrawer
      css={hideWhenDesktop}
      width="315px"
      displayDrawer={mobileMenuVisible}
      onClose={handleToggleMobileMenu}
    >
      {{
        header: () => (
          <PLPMobileControlsHeader>
            <HeaderTxt>Filter and sort</HeaderTxt>
            <CloseIcon
              onClick={handleToggleMobileMenu}
              height={20}
              width={20}
              css={css`
                cursor: pointer;
              `}
              data-testid="PLPMobileControls__close"
            />
          </PLPMobileControlsHeader>
        ),
        content: (
          <PLPMobileControlsContainer data-testid="PLPMobileControls">
            {mobileMenuVisible && (
              <PLPMobileControlsInnerContainer>
                <AccordionV2
                  allowMultiple={false}
                  arrowOnLeft
                  collapsed
                  items={[
                    {
                      id: 0,
                      hed: (
                        <>
                          <FilterSortHeader>Sort</FilterSortHeader>
                          {currentSort && <FilterSortSubheader>{getSortTxt()}</FilterSortSubheader>}
                        </>
                      ),
                      dek: (
                        <RadioFieldsContainer>
                          {sortOptions.map(opt => (
                            <RadioField
                              checked={((opt.key as unknown) as string) === `${currentSort}`}
                              id={(opt.key as unknown) as string}
                              key={`PLPMobileControls__Sort--${opt.key}`}
                              label={opt.description}
                              onChange={() => {
                                onSort((opt.key as unknown) as string);
                              }}
                            />
                          ))}
                        </RadioFieldsContainer>
                      ),
                    },
                    {
                      id: 1,
                      hed: (
                        <>
                          <FilterSortHeader>Layout</FilterSortHeader>
                          {layoutType && (
                            <FilterSortSubheader>{getLayoutTxt()}</FilterSortSubheader>
                          )}
                        </>
                      ),
                      dek: (
                        <RadioFieldsContainer>
                          {segments.map(seg => (
                            <RadioField
                              checked={seg.key === layoutType}
                              id={seg.key}
                              key={`PLPMobileControls__Layout--${seg.key}`}
                              label={seg.text}
                              onChange={() => {
                                handleToggleLayoutType(seg.key);
                              }}
                            />
                          ))}
                        </RadioFieldsContainer>
                      ),
                    },
                    {
                      id: 2,
                      hed: (
                        <>
                          <FilterSortHeader>
                            Style{motifs.length > 0 ? ` (${motifs.length})` : ''}
                          </FilterSortHeader>
                          {motifs.length > 0 && (
                            <FilterSortSubheader>
                              {getNormalizedFilterTxt(motifs)}
                            </FilterSortSubheader>
                          )}
                        </>
                      ),
                      dek: renderStyleCheckboxes(),
                    },
                    {
                      id: 3,
                      hed: (
                        <>
                          <FilterSortHeader>
                            Colors{colors.length > 0 ? ` (${colors.length})` : ''}
                          </FilterSortHeader>
                          {colors.length > 0 && (
                            <FilterSortSubheader>
                              {getNormalizedFilterTxt(colors)}
                            </FilterSortSubheader>
                          )}
                        </>
                      ),
                      dek: renderColorCheckboxes(),
                    },
                  ]}
                />
              </PLPMobileControlsInnerContainer>
            )}
          </PLPMobileControlsContainer>
        ),
        footer: () => (
          <PLPMobileControlsFooter>
            <LinkV2 onClick={handleClickClear} data-testid="PLPMobileControls__clear">
              Clear
            </LinkV2>
            <ButtonV3
              onClick={handleToggleMobileMenu}
              variant="primary"
              data-testid="PLPMobileControls__show"
            >
              Show {resultsCount ? `${resultsCount}` : ''} results
            </ButtonV3>
          </PLPMobileControlsFooter>
        ),
      }}
    </BaseSideDrawer>
  );
};

export default PLPMobileControls;
